import type { Theme, ThemePatch } from './types';

/**
 * Nocturne — transcribed verbatim from
 * `_ds/nocturne-.../styles.css`. This is the fallback every tenant starts from
 * and the source of truth for visual fidelity. Do not tune values here to fix a
 * screen; fix the screen, or override in the CMS.
 */
export const nocturne: Theme = {
  version: 'nocturne-1',

  brand: {
    name: 'Nocturne Con',
    shortName: 'Nocturne',
    logoUrl: null,
    splashImageUrl: null,
    splashBackground: '#161826',
    tagline: 'Convention companion',
  },

  colors: {
    bg: '#161826',
    surface: '#232532',
    text: '#e9e9ed',
    accent: '#9184d9',
    accent2: '#a7a1db',
    // styles.css uses color-mix(in srgb, #e9e9ed 16%, transparent); resolved here
    // so React Native — which has no color-mix — gets the same value.
    divider: 'rgba(233,233,237,0.16)',

    neutral: {
      100: '#f3f5fe', 200: '#e4e7f5', 300: '#cfd3e5', 400: '#b2b6ca', 500: '#9397ab',
      600: '#75798c', 700: '#595d6c', 800: '#3f424d', 900: '#292b31',
    },
    accentRamp: {
      100: '#f5f4ff', 200: '#e7e5fe', 300: '#d2cefd', 400: '#b5abfc', 500: '#968ae0',
      600: '#796cbf', 700: '#5d5294', 800: '#423a6a', 900: '#2b2741',
    },
    accent2Ramp: {
      100: '#f5f4ff', 200: '#e7e5fe', 300: '#d2cefd', 400: '#b5afe8', 500: '#9690c9',
      600: '#7972a9', 700: '#5c5783', 800: '#423e5d', 900: '#2b293a',
    },

    section: '#262a60',
    sectionGlow: '#353b80',
    sectionGhost: '#4c5397',

    tracks: {
      Panel: '#9184d9',
      Screening: '#75798c',
      Special: '#b5abfc',
      Autographs: '#5d5294',
      Workshop: '#595d6c',
      Gaming: '#7972a9',
    },
  },

  type: {
    heading: 'Inter',
    body: 'Inter',
    mono: 'ui-monospace, Menlo, monospace',
    headingWeight: 500,
    webFontUrl:
      'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap',
  },

  // Density 0.70x — baked into the scale, per the system readme.
  space: { s1: 2.8, s2: 5.6, s3: 8.4, s4: 11.2, s6: 16.8, s8: 22.4 },
  radius: { sm: 4, md: 8, lg: 14 },
  shadow: {
    sm: '0 0 0 1px #3f424d',
    md: '0 0 0 1px #595d6c, 0 6px 18px rgba(0,0,0,0.55)',
    lg: '0 0 0 1px #9397ab, 0 16px 40px rgba(0,0,0,0.65)',
  },
  ruleFade: 48,
};

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Deep-merges a CMS patch onto the Nocturne base. Missing keys keep their default. */
export function mergeTheme(base: Theme, patch?: ThemePatch | null): Theme {
  if (!patch) return base;
  const out: Record<string, unknown> = { ...(base as unknown as Record<string, unknown>) };
  for (const [key, value] of Object.entries(patch)) {
    const current = out[key];
    if (isPlainObject(value) && isPlainObject(current)) {
      out[key] = { ...current, ...value };
      // one more level for the nested ramps and track map
      for (const [k2, v2] of Object.entries(value)) {
        const cur2 = (current as Record<string, unknown>)[k2];
        if (isPlainObject(v2) && isPlainObject(cur2)) {
          (out[key] as Record<string, unknown>)[k2] = { ...cur2, ...v2 };
        }
      }
    } else if (value !== undefined) {
      out[key] = value;
    }
  }
  return out as unknown as Theme;
}

/** Fallback track colour for a track the CMS added but did not colour. */
export function trackColor(theme: Theme, track: string): string {
  return theme.colors.tracks[track] ?? theme.colors.neutral[700];
}
